<template>
  <v-row>
    <v-breadcrumbs :items="crumbs" divider="/"></v-breadcrumbs>
  </v-row>
  <v-row>
    <v-col cols="12" md="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">txns</v-card-title>
          <template v-slot:append>
            <v-btn icon="mdi-lightbulb-on-outline" variant="text" @click="backfillSuggestions" :loading="isBackfilling" :class="{ 'pulse-success': backfillSuccess }" v-tooltip="'suggest categories'"></v-btn>
            <v-btn icon="mdi-minus-thick" :variant="showIgnoredTxns ? 'tonal' : 'text'" @click="this.showIgnoredTxns = !this.showIgnoredTxns" v-tooltip="'show ignored txns'"></v-btn>
            <v-btn icon="mdi-upload" variant="text" @click="showFileModal = true" v-tooltip="'upload txns'"></v-btn>
          </template>  
        </v-card-item>
        <v-divider></v-divider>
          <v-card-text>
            <v-container class="table-scroll">
              <table>
                <tr>
                  <th>date</th>
                  <th>description</th>
                  <th>amount</th>
                  <th>merchant</th>
                  
                  <th style="width: 225px">expense category</th>
                  <th style="width: 50px"></th>
                  <th style="width: 50px"></th>
                </tr>
                <template v-for="txn in bank_txns" :key="txn.id">
                  <tr v-if="displayTxn(txn)">
                    <td @click="showEditModal(txn)" class="txn_text">{{ showDateFormat(txn.txn_date) }}</td>
                    <td @click="showEditModal(txn)" class="txn_text">{{ txn.txn_description }}</td>
                    <td @click="showEditModal(txn)" class="txn_text">{{ txn.amount }}</td>
                    <td @click="showEditModal(txn)" class="txn_text">{{ txn.bank_merchant_name }}</td>
                    
                    <td style="vertical-align: middle">
                      <v-select
                        :clearable="this.isClearable(txn.id)"
                        :bg-color="this.getExpenseCategorySelectColour(txn)"
                        density="comfortable"
                        hide-details
                        class="mb-3"
                        label=""
                        v-model="selected_expense[txn.id]"
                        :items="this.expenses"
                        item-title="identifier"
                        item-value="id"
                        :menu-props="{ location: 'bottom end' }"
                        @update:modelValue="persistExpenseCategory(txn)"
                      ></v-select>
                    </td>
                    <td>
                      <Transition mode="out-in" name="fade-in">
                        <v-btn v-if="enableCheckButton(txn)" density="comfortable" icon="mdi-check" size="large" class="mb-3" @click="persistExpenseCategory(txn)"></v-btn>
                      </Transition>
                    </td>
                    <td>
                      <v-btn density="comfortable" icon="mdi-minus-thick" :variant="txn.ignore_for_reconciled ? 'tonal' : 'text'" size="large" class="mb-3" @click="ignoreTxn(txn)"></v-btn>
                    </td>
                  </tr>
                </template>
                
              </table>
              <div id="scroll-sentinel" ref="scrollSentinel" style="height: 20px;"></div> <!-- used for infinite scroll -->
            </v-container>
          </v-card-text>
      </v-card>
    </v-col>
  </v-row>
  

  <!-- modals -->
  <BankTxnsFileModalForTxnViewPopup :show="showFileModal" @close="showFileModal = false" :bankAccountId="this.bank_account_summary.id"></BankTxnsFileModalForTxnViewPopup> <!-- upload txns file -->
  <BankTxnsEditModalPopup :show="showEditModalToggle" @close="showEditModalToggle = false" :currentRow="selectedRow"></BankTxnsEditModalPopup> <!-- edit a record -->

</template>

<script>
import axios from 'axios';
import { mapState, mapMutations} from 'vuex';
import moment from 'moment';
import BankTxnsFileModalForTxnViewPopup from './modals/BankTxnsFileModalForTxnViewPopup.vue';
import BankTxnsEditModalPopup from './modals/BankTxnsEditModalPopup.vue';

export default {
  components: {
    BankTxnsFileModalForTxnViewPopup,
    BankTxnsEditModalPopup

  },
  data() {
    return {
      bank_txns: [],
      bank_account_summary: {},
      showFileModal: false,
      showEditModalToggle: false,
      selectedRow: {},
      expenses: [],
      selected_expense: {},
      bank_txn_expense_category_state: {},
      bank_txns_page: 1,
      showIgnoredTxns: false,
      isBackfilling: false,
      backfillSuccess: false,
      observer: null,
      is_loading: false, // Recommended to prevent duplicate calls
    };
  },
  async created() {
    this.getBankAccountSummaryData();
    this.getBankTxns();
    this.getExpenses();
  },
  computed: {
    ...mapState({
      token: "token"
    }),
    crumbs() {
      return [
        {
          title: 'bank accounts',
          disabled: false,
          href: '/bankaccountsummary'
        },
        {
          title: this.bank_account_summary.account_nickname,
          disabled: true,
          href: ''
        }
      ];
    },
  },
  mounted() {
    this.setupIntersectionObserver();
  },
  unmounted() {
    if (this.observer) {
      this.observer.disconnect();
    }

  },
  methods: {
      ...mapMutations(["showSnackbar"]),
    async getBankAccountSummaryData(){
      try {
      const response  = await axios.get("/bank/accounts/"+this.$route.params.id,
      {
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      });
          this.bank_account_summary = response.data;
      
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load bank account summary' });
      }
    },
    async getBankTxns() {
      if (this.is_loading) return; // Prevent "double-tap" requests
      
      this.is_loading = true;
      try {
        const response = await axios.get("/bank/txns/bankaccount/" + this.$route.params.id, {
          headers: { Authorization: `Bearer ${this.token}` },
          params: { page: this.bank_txns_page }
        });

        const data = response.data;
        
        // If the backend returns no data, we can stop observing
        if (data.length === 0) {
          this.observer.disconnect();
          return;
        }

        data.forEach(txn => {
          this.bank_txns.push(txn);
          if(txn.expense_category_id) {
            this.selected_expense[txn.id] = txn.expense_category_id;
            this.bank_txn_expense_category_state[txn.id] = "persisted";
          }
          else if(txn.suggested_expense_category_id) {
            this.selected_expense[txn.id] = txn.suggested_expense_category_id;
            this.bank_txn_expense_category_state[txn.id] = "suggested";
          }
          else {
            this.bank_txn_expense_category_state[txn.id] = "awaiting";
          }
        });

        this.bank_txns_page++;
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load bank transactions' });
      } finally {
        this.is_loading = false;
      }
    },

    resetLocalBankTxns(){
      this.bank_txns = [];
      this.bank_txns_page = 1
    },
    async getExpenses(){
      try {
      const response  = await axios.get("/expenses/categories/",
      {
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      });
          this.expenses = response.data;
  
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load expense categories' });
      }
    },
    showDateFormat(rawDate){
      return moment(rawDate).format('DD/MM/YY');
    },
    showEditModal(row){
     this.selectedRow=row;
     this.showEditModalToggle = true;
    },
    async persistExpenseCategory(txn){
      try {
        const { data } = await axios.request({
          url: "/bank/txns/expensecategory/" + txn.id,
          method: "PUT",
          headers: {
              Authorization: `Bearer ${this.token}`
          },
          data: {
            expense_category_id: this.selected_expense[txn.id]
          }
        });

          if (this.selected_expense[txn.id] != null){ // update the select button state which drive the display of the check button and it's colour
            this.bank_txn_expense_category_state[txn.id] = 'persisted';
          }
          else {
            this.bank_txn_expense_category_state[txn.id] = '';
          }
          
      } catch (error) {
        this.showSnackbar({ message: 'Failed to update expense category' });
      }
    },
    async ignoreTxn(txn){ // acts a toggle
      try {
        const { data } = await axios.request({
          url: "/bank/txns/ignore/" + txn.id,
          method: "PUT",
          headers: {
              Authorization: `Bearer ${this.token}`
          },
        });

          // this.removeTxnFromLocalStorage(response.data); need to tweak this.

          var foundIndex = this.bank_txns.findIndex(x => x.id == txn.id);
          this.bank_txns[foundIndex].ignore_for_reconciled = data.ignore_for_reconciled;

      } catch (error) {
        this.showSnackbar({ message: 'Failed to update transaction ignore status' });
      }
    },
    enableCheckButton(txn){
      if(this.bank_txn_expense_category_state[txn.id] == "suggested"){ // only show check button when a suggested expense has been made
        return true;
      }
      else{  
        return false;
      }
    },
    getExpenseCategorySelectColour(txn){
      if(this.bank_txn_expense_category_state[txn.id] == "persisted"){
        return "rgba(0, 255, 0, 0.1)"; // green for done
      }
      else if(this.bank_txn_expense_category_state[txn.id] == "suggested"){
        return "rgba(243, 156, 18,0.1)"; // orange for suggested
      }
      else{
        return "rgba(255, 0, 0,0.1)"; // red for needs to be selected
      }
    },
    setupIntersectionObserver() {
      const options = {
        root: null, // use the browser viewport
        rootMargin: '0px 0px 200px 0px', // Trigger 200px BEFORE the user hits the bottom
        threshold: 0.1
      };

      this.observer = new IntersectionObserver((entries) => {
        // If the sentinel is visible AND we aren't already fetching data
        if (entries[0].isIntersecting && !this.is_loading) {
          this.getBankTxns();
        }
      }, options);

      this.observer.observe(this.$refs.scrollSentinel);
    },
    displayTxn(txn){
      if(!txn.ignore_for_reconciled){
        return true;
      }
      else{ // txn has been set to ignore
        if(this.showIgnoredTxns){ // user wants to see ignore txns
          return true;
        }
        else{
          return false;
        }
      }
    },
    async backfillSuggestions(){
      this.isBackfilling = true;
      try {
        const response = await axios.post("/bank/txns/backfill-suggestions/" + this.$route.params.id, {}, {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        this.resetLocalBankTxns();
        this.getBankTxns();
        this.backfillSuccess = true;
        setTimeout(() => { this.backfillSuccess = false; }, 1500);
      } catch (error) {
        this.showSnackbar({ message: 'Failed to suggest categories' });
      } finally {
        this.isBackfilling = false;
      }
    },
    isClearable(txn_id){ // enable clearable on a select only when an expense has been persisted
      if (this.bank_txn_expense_category_state[txn_id] == "persisted"){
        return true;
      }
      else {
        return false
      }
    },

  }
};
</script>


<style scoped>
table{
  width: 100%;
}

table tr{
  cursor: pointer;
}

.fade-in-enter-active {
  transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.fade-in-leave-active {
  transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}

.green_text{
  color: green;
}

.red_text{
  color: red;
}

.txn_text{
  font-weight: 300;
}

.pulse-success {
  animation: pulse 0.5s ease-in-out 1;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.3); }
}

</style>